/**
 * Authentication API
 * Handles all authentication-related endpoints
 */

import { post, get } from "./baseClient";

// --- Request/Response Types ---

export interface RegisterRequest {
  email: string;
  password: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface AuthResponse {
  token: string;
  user_id: string;
  session_id?: string; // Optional, might be used later for resuming sessions
}

export interface User {
  user_id: string;
  email: string;
  account_tier_id: number;
  created_at: string;
  updated_at: string;
  email_verified: boolean;
  beta_acknowledged: boolean; // Added for beta agreement status
}

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

export interface ChangePasswordResponse {
  message: string;
}

export interface ResendVerificationResponse {
  message: string;
}

export interface RequestPasswordResetResponse {
  message: string;
}

export interface ResetPasswordResponse {
  message: string;
}

// --- API Functions ---

/**
 * Registers a new user
 */
export async function registerUser(
  credentials: RegisterRequest
): Promise<AuthResponse> {
  return post<AuthResponse>("/auth/register", credentials, false);
}

/**
 * Logs in a user
 */
export async function loginUser(
  credentials: LoginRequest
): Promise<AuthResponse> {
  return post<AuthResponse>("/auth/login", credentials, false);
}

/** The one-click sign-in link from a product email, as its page reads it. */
export interface EmailSignInRequest {
  u: string;
  c: string;
  e: string;
  t: string;
}

/**
 * Trades an email's one-click sign-in link for a session token.
 * Each link works once.
 */
export async function signInFromEmail(
  link: EmailSignInRequest
): Promise<{ token: string }> {
  return post<{ token: string }>("/public/email-signin", link, false);
}

/**
 * Fetches the profile of the currently authenticated user
 */
export async function getMe(): Promise<User> {
  return get<User>("/me", true);
}

/**
 * Resends verification email
 */
export async function resendVerificationEmail(
  email: string
): Promise<ResendVerificationResponse> {
  return post<ResendVerificationResponse>(
    "/auth/resend-verification",
    { email },
    false
  );
}

/**
 * Changes user password
 */
export async function changePassword(
  data: ChangePasswordRequest
): Promise<ChangePasswordResponse> {
  return post<ChangePasswordResponse>("/me/password", data, true);
}

/**
 * Requests a password reset
 */
export async function requestPasswordReset(
  email: string
): Promise<RequestPasswordResetResponse> {
  return post<RequestPasswordResetResponse>(
    "/auth/request-password-reset",
    { email },
    false
  );
}

/**
 * Resets password using reset token
 */
export async function resetPassword(
  token: string,
  newPassword: string
): Promise<ResetPasswordResponse> {
  return post<ResetPasswordResponse>(
    "/auth/reset-password",
    { token, new_password: newPassword },
    false
  );
}
